import { Controller } from "@hotwired/stimulus"
import { formatMoney } from "./utils/money"

// goal-preview — preview vivo do form de meta: espelha nome + valor da meta e
// pinta/troca o ícone conforme o usuário escolhe cor (bolinha + presets) e ícone
// (grade). Os SVGs dos ícones são renderizados server-side em cada célula da grade
// (icon helper); o preview CLONA o SVG da célula selecionada — nada de SVG no JS.
// A cor e o ícone persistidos vivem em hidden fields. CSP-safe.
//
// Uso: data-controller="goal-preview" com:
//   inputs: nameInput, targetInput, picker
//   hidden: colorValue, iconValue
//   saída:  chip (o quadradinho do ícone no preview), nameOut, subOut
//   grade:  iconCell (cada célula; data-icon = nome), swatch (cada preset; data-color)
//   values: defaultColor (cor esmeralda quando não há cor), nameFallback, targetPrefix
export default class extends Controller {
  static targets = [
    "nameInput", "targetInput", "picker", "colorValue", "iconValue",
    "chip", "nameOut", "subOut", "iconCell", "swatch"
  ]
  static values = {
    defaultColor: String,   // hex esmeralda usado quando não há cor escolhida
    nameFallback: String,
    targetPrefix: String,   // ex.: "Meta de "
    currencySymbol: String, // ex.: "R$" - prefixo do valor formatado no preview
    currency: Object        // meta da moeda (FormatHelper::CURRENCY_META)
  }

  connect() {
    this._color = (this.hasColorValueTarget && this.colorValueTarget.value.trim()) || this.defaultColorValue
    this._icon = (this.hasIconValueTarget && this.iconValueTarget.value.trim()) || this._firstIconName()
    this.render()
    this._renderIcon()
  }

  // ---- Texto ----
  render() {
    if (this.hasNameOutTarget) {
      const v = this.hasNameInputTarget ? this.nameInputTarget.value.trim() : ""
      this.nameOutTarget.textContent = v || this.nameFallbackValue || ""
    }
    if (this.hasSubOutTarget) {
      const cents = this._targetCents()
      this.subOutTarget.textContent = cents > 0
        ? `${this.targetPrefixValue || ""}${this._formatMoney(cents)}`
        : ""
    }
  }

  // Lê os dígitos do input (cru "121212" ou mascarado "1.212,12") como centavos.
  // À prova da ordem de inicialização com o mask controller: sempre parte dos dígitos.
  _targetCents() {
    if (!this.hasTargetInputTarget) return 0
    const digits = (this.targetInputTarget.value || "").replace(/\D/g, "")
    return digits ? parseInt(digits, 10) : 0
  }

  // Formata pelo meta da moeda do app (mesmo algoritmo do servidor - utils/money).
  // Antes fixava pt-BR e 2 casas aqui: em JPY/CLP inventava decimais que o servidor
  // não renderiza, e os separadores saíam trocados fora de BRL.
  _formatMoney(cents) {
    return formatMoney(cents, this.hasCurrencyValue ? this.currencyValue : { symbol: this.currencySymbolValue })
  }

  // ---- Cor ----
  pickColor() {
    this._color = this.pickerTarget.value
    this._commitColor()
  }

  presetColor(event) {
    const c = event.currentTarget.dataset.color
    if (!c) return
    this._color = c
    if (this.hasPickerTarget) this.pickerTarget.value = c
    this._commitColor()
  }

  _commitColor() {
    if (this.hasColorValueTarget) this.colorValueTarget.value = this._color
    this._renderIcon()
    this._syncSwatches()
  }

  // ---- Ícone ----
  pickIcon(event) {
    const cell = event.currentTarget
    this._icon = cell.dataset.icon
    if (this.hasIconValueTarget) this.iconValueTarget.value = this._icon
    this._renderIcon()
    this._syncCells()
  }

  _renderIcon() {
    // Clona o SVG da célula do ícone atual para o chip do preview.
    if (this.hasChipTarget) {
      const cell = this._cellFor(this._icon)
      const svg = cell && cell.querySelector("svg")
      if (svg) this.chipTarget.innerHTML = svg.outerHTML
      // fundo em ~15% + texto na cor cheia (via style; paridade com o helper).
      this.chipTarget.style.color = this._color
      this.chipTarget.style.background = `color-mix(in oklch, ${this._color} 15%, transparent)`
    }
  }

  // ---- helpers de sincronização visual ----
  _syncCells() {
    this.iconCellTargets.forEach((c) => {
      const on = c.dataset.icon === this._icon
      c.classList.toggle("border-primary", on)
      c.classList.toggle("bg-primary/12", on)
      c.classList.toggle("text-primary", on)
      c.classList.toggle("text-muted-foreground", !on)
    })
  }

  _syncSwatches() {
    this.swatchTargets.forEach((s) => {
      const on = (s.dataset.color || "").toLowerCase() === (this._color || "").toLowerCase()
      s.classList.toggle("ring-2", on)
      s.classList.toggle("ring-primary", on)
      s.classList.toggle("ring-offset-2", on)
      s.classList.toggle("ring-offset-background", on)
    })
  }

  _cellFor(name) {
    return this.iconCellTargets.find((c) => c.dataset.icon === name) || this.iconCellTargets[0]
  }

  _firstIconName() {
    return this.hasIconCellTarget ? this.iconCellTargets[0].dataset.icon : "target"
  }
}
